import React, { useRef } from "react";
import {
  Box,
  IconButton,
  Card,
  Typography,
  LinearProgress,
  Grid2 as Grid,
  Chip,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import "swiper/css"; // Core Swiper styles
import "swiper/css/navigation"; // Navigation styles
import { green, orange } from "@mui/material/colors";
import { useClientContext } from "../context/ClientContext";
import { clientGroups } from "../assets/clientData";
import { productHoldingData } from "../assets/productHolding";
import { useState, useEffect } from "react";
import { formatCurrency } from "../utils/helper";

const ProductHoldingsCard = ({ setLevel }) => {
  const { clientName, clientGroup } = useClientContext();
  const [data, setData] = useState(null);

  useEffect(() => {
    // Filter data based on selected client and group
    if (clientName && clientGroup) {
      const filteredData = productHoldingData.filter(
        (data) =>
          data.company_name === clientName &&
          data.group_company ===
            clientGroups.find((group) => group.group_name === clientGroup)
              ?.label
      );

      if (filteredData.length > 0) {
        const processedData = {
          third_party: {
            total_asset:
              filteredData[0].total_assets_under_management == "0.0"
                ? "0.0"
                : filteredData[0].total_assets_under_management || "...",
            funding:
              filteredData[0].funding_amount_growth_achieved_current_fy == "0.0"
                ? "0.0"
                : filteredData[0].funding_amount_growth_achieved_current_fy ||
                  "...",
            target:
              filteredData[0].funding_growth_target_amount_current_fy == "0.0"
                ? "0.0"
                : filteredData[0].funding_growth_target_amount_current_fy ||
                  "...",
            month_to_date:
              filteredData[0].current_account_growth_mtd_percentage == "0.0"
                ? "0.0"
                : filteredData[0].current_account_growth_mtd_percentage ||
                  "...",
            year_to_date:
              filteredData[0].current_account_growth_ytd_percentage == "0.0"
                ? "0.0"
                : filteredData[0].current_account_growth_ytd_percentage ||
                  "...",
          },
          loan: {
            year: `FY ${new Date().getFullYear().toString().slice(-2)}`,
            lending:
              filteredData[0].lending_amount_growth_achieved_current_fy ==
              "0.0"
                ? "0.0"
                : filteredData[0].lending_amount_growth_achieved_current_fy ||
                  "...",
            target:
              filteredData[0].lending_growth_target_amount_current_fy == "0.0"
                ? "0.0"
                : filteredData[0].lending_growth_target_amount_current_fy ||
                  "...",
            total_credit:
              filteredData[0].total_credit_limit == "0.0"
                ? "0.0"
                : filteredData[0].total_credit_limit || "...",
            total_outstanding:
              filteredData[0].total_credit_outstanding == "0.0"
                ? "0.0"
                : filteredData[0].total_credit_outstanding || "...",
            available_credit:
              filteredData[0].available_credit_limit == "0.0"
                ? "0.0"
                : filteredData[0].available_credit_limit || "...",
            cash_loan:
              filteredData[0].total_cash_loan_outstanding == "0.0"
                ? "0.0"
                : filteredData[0].total_cash_loan_outstanding || "...",
            cash_loan_limit:
              filteredData[0].total_cash_loan_limit == "0.0"
                ? "0.0"
                : filteredData[0].total_cash_loan_limit || "...",
            non_cash_loan:
              filteredData[0].total_non_cash_loan_outstanding == "0.0"
                ? "0.0"
                : filteredData[0].total_non_cash_loan_outstanding || "...",
            non_cash_loan_limit:
              filteredData[0].total_non_cash_loan_limit == "0.0"
                ? "0.0"
                : filteredData[0].total_non_cash_loan_limit || "...",
          },
        };
        setData(processedData);
      }
    }
  }, [clientName, clientGroup]);

  // Unique class names for navigation buttons
  const prevBtnRef = useRef(null);
  const nextBtnRef = useRef(null);

  const handleLevel = (level) => {
    setLevel(level);
  };

  if (!data) return null;

  return (
    <Box
      className="rounded-md py-1 px-4"
      style={{
        boxShadow: "0px 2px 20px rgba(0, 0, 0, 0.1)",
        height: "60vh", // Fixed height
        overflowY: "scroll", // Enable vertical scrolling
      }}
    >
      <Box className="my-2">
        <Typography
          variant="subtitle4"
          fontWeight="bold"
          my={0.5}
          sx={{ fontSize: "0.875rem" }}
        >
          Third Party Fund (DPK) Accounts​
        </Typography>
        <Card sx={{ background: "#eeeeee" }}>
          <Box className="p-2 grid gap-1 text-gray-600 text-[12px]">
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid container spacing={5}>
                <div className="font-bold">Total Assets under Management</div>
                <div className="text-main font-bold">{`${formatCurrency(
                  data.third_party.total_asset
                )} IDR`}</div>
                ​
              </Grid>

              <Grid>
                <IconButton
                  aria-label="<>"
                  sx={{ height: 24, width: 24 }}
                  onClick={() => handleLevel("L2.1")}
                >
                  <ArrowForwardOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Grid>
            </Grid>
            <div>
              Funding Amount Growth Achieved (IDR) :{" "}
              <span className="text-main">
                {formatCurrency(data.third_party.funding)} IDR
              </span>
            </div>
            <div>
              Target (IDR) :{" "}
              <span className="text-main">
                {formatCurrency(data.third_party.target)} IDR
              </span>
            </div>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: "80%", mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    data.third_party.target != "..."
                      ? (data.third_party.funding / data.third_party.target) *
                        100
                      : 0
                  }
                  sx={{ height: 10 }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <div className="text-main">{`${
                  data.third_party.target != "..."
                    ? (data.third_party.funding / data.third_party.target) * 100
                    : 0
                }%`}</div>
              </Box>
            </Box>

            <div className="font-bold">Current Account Growth ​</div>
            <Grid container spacing={5}>
              <Grid>
                <div>Month-to-Date</div>
                <div className="text-main">
                  {data.third_party.month_to_date}%
                </div>
              </Grid>
              <Grid>
                <div>Year-to-Date</div>
                <div className="text-main">
                  {data.third_party.year_to_date}%
                </div>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>
      <Box className="my-">
        <Typography
          variant="subtitle4"
          fontWeight="bold"
          my={0.5}
          sx={{ fontSize: "0.875rem" }}
        >
          Loan Accounts​
        </Typography>
        <Card sx={{ background: "#eeeeee" }}>
          <Box className="p-2 grid gap-1 text-gray-600 text-[12px]">
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="font-bold">{data.loan.year}</div>

              <Grid>
                <IconButton
                  aria-label="<>"
                  sx={{ height: 24, width: 24 }}
                  onClick={() => handleLevel("L2.2")}
                >
                  <ArrowForwardOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Grid>
            </Grid>
            <div>
              Lending Amount Growth Achieved (IDR) :{" "}
              <span className="text-main">
                {formatCurrency(data.loan.lending)} IDR
              </span>
            </div>
            <div>
              Target (IDR) :{" "}
              <span className="text-main">
                {formatCurrency(data.loan.target)} IDR
              </span>
            </div>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: "80%", mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    data.loan.target !== "..." && data.loan.target !== "0.0"
                      ? Math.min(
                          ((parseFloat(data.loan.lending) || 0) /
                            (parseFloat(data.loan.target) || 1)) *
                            100,
                          100
                        )
                      : 0
                  }
                  sx={{ height: 10 }}
                />
              </Box>

              <Box sx={{ minWidth: 35 }}>
                <div className="text-main">
                  {data.loan.target !== "..." && data.loan.target !== "0.0"
                    ? `${Math.min(
                        ((parseFloat(data.loan.lending) || 0) /
                          (parseFloat(data.loan.target) || 1)) *
                          100,
                        100
                      ).toFixed(1)}%`
                    : "0.0%"}
                </div>
              </Box>
            </Box>

            <Grid container spacing={15} my={1}>
              <Grid container direction={"column"} rowSpacing={0.3}>
                <div className="font-bold">Total Credit Limit</div>
                <div className="text-center text-main">
                  {formatCurrency(data.loan.total_credit)} IDR
                </div>
              </Grid>
              <Grid container direction={"column"} rowSpacing={0.3}>
                <div className="font-bold">Total Outstanding</div>
                <div className="text-center text-main">
                  {formatCurrency(data.loan.total_outstanding)} IDR
                </div>
              </Grid>

              <Grid container direction={"column"} rowSpacing={0.3}>
                <div className="font-bold">Available Credit</div>
                <div className="text-center text-main">
                  {formatCurrency(data.loan.available_credit)} IDR
                </div>
              </Grid>
            </Grid>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid size={3}>
                <div className="font-bold">Cash Loan</div>
              </Grid>
              <Grid size={3}>
                <div className="text-main">{`${formatCurrency(
                  data.loan.cash_loan
                )} / ${formatCurrency(data.loan.cash_loan_limit)}`}</div>
              </Grid>

              <Grid size={2}>
                <Chip
                  color="success"
                  label={`${
                    data.loan.cash_loan_limit != "..." &&
                    data.loan.cash_loan_limit !== "0.0"
                      ? (
                          (data.loan.cash_loan / data.loan.cash_loan_limit) *
                          100
                        ).toFixed(1)
                      : 0
                  }% Utilized `}
                  size="small"
                  sx={{
                    fontSize: 10,
                    background: green[100],
                    color: green[700],
                  }}
                />
              </Grid>
            </Grid>
            <Grid
              container
              sx={{ alignItems: "center", justifyContent: "flex-start" }}
            >
              <Grid size={3}>
                <div className="font-bold">Non-Cash Loan</div>
              </Grid>

              <Grid size={3}>
                <div className="text-main">{`${formatCurrency(
                  data.loan.non_cash_loan
                )} / ${formatCurrency(data.loan.non_cash_loan_limit)}`}</div>
              </Grid>

              <Grid size={2}>
                <Chip
                  label={`${
                    data.loan.non_cash_loan_limit != "..." &&
                    data.loan.non_cash_loan_limit !== "0.0"
                      ? ((data.loan.non_cash_loan /
                          data.loan.non_cash_loan_limit) *
                        100
                      ).toFixed(1)
                      : 0
                  }% Utilized `}
                  size="small"
                  sx={{
                    fontSize: 10,
                    background: orange[100],
                    color: orange[700],
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default ProductHoldingsCard;
